import { useMemo } from 'react';
import { useAppSelector } from '../../../app/hooks';
import {
  selectColumnsExistInDbData,
  selectScanDataTableExists,
} from '../../duckDB/duckDBSlice';
import { DuckQueryReturnOneType } from '../../duckDB/hooks/useDuckQueryScanData';
import { selectCurrMeasure } from '../../select/selectSlice';
import {
  ScanDataEnum,
  ScanDataMeasureEnum,
  convertMeasureCubeRanchToScanDataBlock,
} from '../scanDataDefinitions';
import { useScanDataQuery } from './useScanDataQuery';

/**
 * Interface for the expected structure of query data returned from DuckDB.
 */
interface QueryData {
  sum_estimated_count: number | null;
  sum_distance_traveled: number | null;
  block_avg_distance_traveled: number | null;
  block_avg_standard: number | null;
}

/**
 * Extends DuckQueryReturnOneType with a flag to indicate the presence of traveled distance data.
 */
interface Response extends DuckQueryReturnOneType<number> {
  hasTraveledDistance: boolean;
}

/**
 * Custom hook to calculate block average based on current measure and data availability.
 * Dynamically constructs the SQL query based on these conditions, addressing two primary scenarios:
 * 1. block_avg_distance_traveled: Calculated as sum_estimated_count / sum_distance_traveled for measures
 *    where distance_traveled data is available and the measure is count_per_meter. This method provides
 *    a more precise average by considering the distance traveled.
 * 2. block_avg_standard: Calculated as AVG(currScanDataMeasure) for cases where distance_traveled data is
 *    unavailable or the measure is not count_per_meter, offering a straightforward average calculation.
 *
 * @param {Object} options - The options for the hook, including a skip flag to control query execution.
 * @returns {Response} - The query result, including the calculated average and a flag for traveled distance usage.
 */
export const useBlockAverage = ({ skip }: { skip?: boolean }): Response => {
  const scanDataTableExists = useAppSelector(selectScanDataTableExists);
  const columnsExistInDbData = useAppSelector(selectColumnsExistInDbData);
  const currMeasure = useAppSelector(selectCurrMeasure);

  // Converts the current measure to a format compatible with scan data definitions.
  const currScanDataMeasure =
    convertMeasureCubeRanchToScanDataBlock(currMeasure);

  // Determines the columns to query based on current measure and data availability.
  const cols = useMemo(() => {
    // Default to average calculation for non-count_per_meter measures or when distance_traveled column is missing.
    if (
      currScanDataMeasure !== ScanDataMeasureEnum.count_per_meter ||
      !columnsExistInDbData?.data?.[ScanDataEnum.traveled_distance]
    ) {
      return [`AVG(${currScanDataMeasure}) as block_avg_standard`];
    }

    // For count_per_meter measures with traveled distance data, calculate both sum and average.
    return [
      `SUM(${ScanDataEnum.estimated_count}) as sum_estimated_count`,
      `SUM(${ScanDataEnum.traveled_distance}) as sum_distance_traveled`,
      `sum_estimated_count / sum_distance_traveled as block_avg_distance_traveled`,
      `AVG(${currScanDataMeasure}) as block_avg_standard`,
    ];
  }, [columnsExistInDbData?.data, currScanDataMeasure]);

  // Fetches data using the constructed query, skipping if necessary or if the scan data table doesn't exist.
  const queryResult = useScanDataQuery<QueryData>({
    cols,
    skip: skip || !scanDataTableExists,
    useUnitsTransformSubstringMatching: true,
  });

  // Determines if valid traveled distance data is present for calculation.
  const hasValidDistanceTraveledAverage = Boolean(
    queryResult.data?.[0]?.sum_distance_traveled &&
      queryResult.data?.[0]?.block_avg_distance_traveled
  );
  const useDistanceTraveledMethod =
    currScanDataMeasure === ScanDataMeasureEnum.count_per_meter &&
    hasValidDistanceTraveledAverage;

  const average = useDistanceTraveledMethod
    ? queryResult.data?.[0]?.block_avg_distance_traveled
    : queryResult.data?.[0]?.block_avg_standard;

  return {
    ...queryResult,
    data: average || null,
    hasTraveledDistance: hasValidDistanceTraveledAverage,
  };
};
